import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DnsIcon from '@mui/icons-material/Dns'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { ServerOption, getServerOptions } from '../config/server-options'

interface NetworkRecoveryDialogProps {
  open: boolean
  currentServerUrl: string | null
  onClose: () => void
  onSelectServer: (serverUrl: string) => void
}

const normalizeUrl = (url: string) => url.trim().replace(/\/+$/, '')

const NetworkRecoveryDialog = ({
  open,
  currentServerUrl,
  onClose,
  onSelectServer,
}: NetworkRecoveryDialogProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [selectedUrl, setSelectedUrl] = useState(currentServerUrl || '')

  const serverOptions = useMemo(() => {
    const options = getServerOptions()
    const normalizedCurrentUrl = currentServerUrl ? normalizeUrl(currentServerUrl) : ''
    const hasCurrentUrl = options.some((option) => option.url === normalizedCurrentUrl)

    if (normalizedCurrentUrl && !hasCurrentUrl) {
      return [
        ...options,
        {
          id: `current-${normalizedCurrentUrl}`,
          name: 'Current custom server',
          url: normalizedCurrentUrl,
        },
      ]
    }

    return options
  }, [currentServerUrl])

  useEffect(() => {
    if (open) {
      setSelectedUrl(currentServerUrl || serverOptions[0]?.url || '')
    }
  }, [currentServerUrl, open, serverOptions])

  const selectServer = () => {
    if (!selectedUrl) return
    onSelectServer(selectedUrl)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
          boxShadow: '0 26px 80px rgba(0,0,0,0.32)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.background.default, isDark ? 0.72 : 0.56),
            backdropFilter: 'blur(16px)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          background: isDark
            ? 'linear-gradient(135deg, #004D40 0%, #009688 55%, #00695C 100%)'
            : 'linear-gradient(135deg, #FF8F00 0%, #FFD653 55%, #FFAB40 100%)',
          color: isDark ? theme.palette.common.white : alpha(theme.palette.common.black, 0.78),
          pb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: alpha(theme.palette.common.white, isDark ? 0.14 : 0.34),
              border: `1px solid ${alpha(theme.palette.common.white, 0.22)}`,
              borderRadius: 2,
              display: 'flex',
              height: 42,
              justifyContent: 'center',
              width: 42,
            }}
          >
            <WifiOffIcon />
          </Box>
          <Box minWidth={0}>
            <Typography variant="h6" fontWeight={900}>
              Connection interrupted
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.78 }}>
              The last request could not reach the selected server.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Typography color="text.secondary">
            Check the network connection, then choose an available backend server below.
          </Typography>

          <Stack spacing={1.25}>
            {serverOptions.map((option: ServerOption) => {
              const isCurrentServer = currentServerUrl && option.url === normalizeUrl(currentServerUrl)
              const isSelected = selectedUrl === option.url

              return (
                <Button
                  key={option.id}
                  onClick={() => setSelectedUrl(option.url)}
                  variant={isSelected ? 'contained' : 'outlined'}
                  startIcon={isSelected ? <CheckCircleIcon /> : <DnsIcon />}
                  sx={{
                    borderRadius: 2,
                    justifyContent: 'flex-start',
                    py: 1.35,
                    textAlign: 'left',
                    textTransform: 'none',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" width="100%" minWidth={0}>
                    <Box flex={1} minWidth={0}>
                      <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                        <Typography noWrap fontWeight={800}>
                          {option.name}
                        </Typography>
                        {isCurrentServer && (
                          <Chip size="small" label="Current" variant={isSelected ? 'filled' : 'outlined'} />
                        )}
                      </Stack>
                      <Typography noWrap variant="caption" sx={{ opacity: 0.78 }}>
                        {option.url}
                      </Typography>
                    </Box>
                  </Stack>
                </Button>
              )
            })}
          </Stack>

          <Divider />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button onClick={selectServer} variant="contained" sx={{ borderRadius: 2, textTransform: 'none' }}>
          Use selected server
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NetworkRecoveryDialog
